import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { HEADER_VIEW_PAGE_CODE_BY_TYPE } from 'constants/headerViewPermissions';
import { resolveHeaderViewGlobality } from 'utils/headerView';
import { getPagePermissions } from 'utils/permissions';

import { getIsFromInternalCompany, getMasterPermissions } from 'store/auth/selectors';

import { getSystem } from './system.selectors';

const selectHeaderViews = createSelector(getSystem, ({ headerViews }) => headerViews);
export const selectHeaderViewsValues = createSelector(selectHeaderViews, Object.values);

export const selectHeaderViewTypePermissions = createSelector([getMasterPermissions], permissions =>
  _.mapValues(HEADER_VIEW_PAGE_CODE_BY_TYPE, pageCode => getPagePermissions(pageCode)(permissions)),
);

export const selectManageableHeaderViewTypes = createSelector(
  [selectHeaderViewTypePermissions],
  permissionsByType =>
    Object.keys(permissionsByType).filter(type => permissionsByType[type].isManageAllowed),
);
export const selectHeaderViewById = memoizeOne(headerViewId =>
  createSelector(selectHeaderViews, headerViews => headerViews[headerViewId]),
);

// The company-default marker is derived from the dedicated company/defaults read rather than a flag
// on LoanHeaderViewDto — that DTO is returned by ~9 endpoints and only the list read could populate
// it, so the flag would serialize false on every other path even when the view IS the default.
export const selectCompanyDefaultHeaderViewIds = createSelector(
  getSystem,
  ({ companyDefaultHeaderViews }) =>
    new Set((companyDefaultHeaderViews ?? []).map(({ headerViewId }) => headerViewId)),
);

export const selectHeaderViewsOptionsByType = memoizeOne(type =>
  createSelector([selectHeaderViewsValues, getIsFromInternalCompany], (items, isInternalCompany) =>
    items
      .filter(item => item.type === type)
      .map(item => ({
        name: item.name,
        value: item.id,
        isGlobal: resolveHeaderViewGlobality(item, isInternalCompany),
      })),
  ),
);

// The SubCompany header view is resolved per-user by the backend from the user's group ->
// LoanHeaderViewRoles bindings (defaulting to the unbound view), fetched via
// getResolvedSubCompanyHeaderViewAction. Read that resolved result — never name-sort the raw
// GET /LoanHeaderView list, which ignores the role bindings so every user sees the first-by-name
// view (DEV-4941).
export const selectSubCompanyHeaderView = createSelector(
  getSystem,
  ({ resolvedSubCompanyHeaderView }) => resolvedSubCompanyHeaderView ?? null,
);
